import { gnosis } from 'viem/chains'

import { AccountMetadata } from '../types'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { links } from '@/ui/constants/links'

export interface GetAccountMetadataArgs {
  savingsToken: TokenSymbol
  underlyingToken: TokenSymbol
  chainId: number
}

export function getAccountMetadata({
  savingsToken,
  underlyingToken,
  chainId,
}: GetAccountMetadataArgs): AccountMetadata {
  return {
    description: getAccountDescription({ savingsToken, underlyingToken, chainId }),
    apyExplainer: getApyExplainer({ savingsToken, underlyingToken, chainId }),
    descriptionDocsLink: getDocsLink({ savingsToken, chainId }),
    apyExplainerDocsLink: getDocsLink({ savingsToken, chainId }),
  }
}

function getAccountDescription({ savingsToken, underlyingToken, chainId }: GetAccountMetadataArgs): string {
  if (chainId === gnosis.id) {
    return 'Deposit your stablecoins into XDAI\u00A0Savings to tap into the DAI\u00A0Savings\u00A0Rate, which grants you a transparent APY in XDAI.'
  }
  if (underlyingToken === TokenSymbol('sDAI')) {
    return 'Deposit your stablecoins into DAI\u00A0Savings to tap into the DAI\u00A0Savings\u00A0Rate, which grants you a transparent APY in DAI.'
  }
  if (savingsToken === TokenSymbol('stUSDS')) {
    return 'stUSDS is a yield-bearing token representing USDS deposited into the stUSDS contract. Its primary function is to provide segregated risk capital for SKY-backed loans inside the lockstake engine.'
  }
  if (savingsToken === TokenSymbol('spUSDC')) {
    return 'Deposit your USDC into Savings\u00A0v2 to earn a transparent APY in USDC. The rate updates automatically as it evolves.'
  }
  if (savingsToken === TokenSymbol('spUSDT')) {
    return 'Deposit your USDT into Savings\u00A0v2 to earn a transparent APY in USDT. The rate updates automatically as it evolves.'
  }
  if (savingsToken === TokenSymbol('spETH')) {
    return 'Deposit your ETH into Savings\u00A0v2 to earn a transparent APY in ETH. The rate updates automatically as it evolves.'
  }
  return `Deposit your stablecoins into ${underlyingToken}\u00A0Savings to tap into the Sky\u00A0Savings\u00A0Rate, which grants you a transparent APY in ${underlyingToken}.`
}

function getApyExplainer({ savingsToken, underlyingToken, chainId }: GetAccountMetadataArgs): string {
  if (chainId === gnosis.id) {
    return 'All interest from the xDAI Bridge on the Ethereum mainnet is being redirected to Gnosis sDAI. The bridge converts its DAI deposits into DAI Savings on the mainnet, which then accumulates the DSR. The DSR is set on mainnet by Sky Ecosystem Governance. Keep in mind that these protocol mechanisms are subject to change.'
  }
  if (underlyingToken === TokenSymbol('DAI')) {
    return 'Current annual interest rate for DAI deposited into the Sky Savings Module. It is determined on-chain by the Sky Ecosystem Governance. Please note that these protocol mechanisms are subject to change.'
  }
  if (savingsToken === TokenSymbol('stUSDS')) {
    return 'The stUSDS rate is variable. The rate changes based on utilization - the portion of USDS currently lent out. The target utilization is 90%. When utilization rises above this level, the rate gradually increases; when it falls below, the rate decreases over time. The minimum rate is the Sky Savings Rate.'
  }
  if (savingsToken.startsWith('sp')) {
    return 'Current annual interest. It is determined on-chain by the Spark Governance. Please note that these protocol mechanisms are subject to change.'
  }
  return 'Current annual interest in the Sky Savings Module. It is determined on-chain by the Sky Ecosystem Governance. Please note that these protocol mechanisms are subject to change.'
}

function getDocsLink({ savingsToken, chainId }: { savingsToken: TokenSymbol; chainId: number }): string {
  if (chainId === gnosis.id) {
    return links.docs.savings.gnosisSdai
  }
  if (savingsToken === TokenSymbol('sDAI')) {
    return links.docs.savings.sdai
  }
  if (savingsToken === TokenSymbol('sUSDC')) {
    return links.docs.savings.susdc
  }
  if (savingsToken === TokenSymbol('stUSDS')) {
    return links.docs.savings.stusds
  }
  if (savingsToken === TokenSymbol('spUSDC')) {
    return links.docs.savings.spusdc
  }
  if (savingsToken === TokenSymbol('spUSDT')) {
    return links.docs.savings.spusdt
  }
  if (savingsToken === TokenSymbol('spETH')) {
    return links.docs.savings.speth
  }

  return links.docs.savings.susds
}
